import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import {
  Box,
  Text,
  Heading,
  Button,
  Image,
  VStack,
  HStack,
  // Divider,
  Textarea,
  Container,
  useBreakpointValue,
  Flex,
  // useMediaQuery,
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import { GiNightSleep, GiSadCrab } from "react-icons/gi";

import {
  instr_relay_start,
  instr_relay_middle,
  instr_relay_end,
  instr_relay_end_opt,
  notify_skip,
  notify_skipped,
  notify_both_skip,
} from "utils/data/constants";
import RelayPrompt from "./RelayPrompt";
import game_not_start_img from "assets/game_not_start.webp";

// Function to count words
const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

function StoryRelayPage() {
  const { storyId } = useParams();

  const [story, setStory] = useState({});

  const [wordCount, setWordCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  // const [isMobile] = useMediaQuery('(max-width: 768px)');
  // console.log('is mobile: ', isMobile);

  const {
    storyTitle,
    starter,
    instruction,
    extraInstruction,
    status,
    round,
    url,
    genre,
    skipAllowed,
    maxWordCount,
    minSegmentWordCount,
    maxSegmentWordCount,
    canSkip,
    wrapUpOptions,
  } = story;

  useEffect(() => {
    api.get(`${API_BASE}/api/relay-story/fetch?storyId=${storyId}`).then((res) => {
      if (res.status == 200) {
        // console.log('prompt story data: ', res.data?.data);
        setStory(res.data);
      }
    });
  }, [storyId]);

  function handleRelayStart(userLead) {
    setIsSubmitting(true);
    api
      .get(
        `${API_BASE}/api/relay-story/start-game?storyId=${storyId}&userLead=${userLead}`
      )
      .then((res) => {
        if (res.status == 200) {
          // console.log('start impromptu story data: ', res.data?.data);
          setStory(res.data);
        }
      })
      .catch(() => console.log("failed to start game, storyy id: ", storyId))
      .finally(() => setIsSubmitting(false));
  }

  function updateStory(draft) {
    setIsSubmitting(true);
    const body = {
      storyId,
      round,
      userLead: story.leader === "student",
      content: draft,
      updateType: 1,
    };
    api
      .post(`${API_BASE}/api/relay-story/update-story`, body)
      .then((res) => {
        if (res.status == 200) {
          console.log("update story, storyId: ", res.data);
        }
      })
      .catch(() => console.log("failed to update story, story id: ", storyId))
      .finally(() => setIsSubmitting(false));
  }

  function writeAndRollStory(draft, type) {
    setIsSubmitting(true);
    const body = {
      storyId,
      round,
      userLead: story.leader === "student",
      content: draft,
      updateType: type,
    };
    api
      .post(`${API_BASE}/api/relay-story/write-and-roll`, body)
      .then((res) => {
        if (res.status == 200) {
          // console.log('write and roll story data: ', res.data?.data);
          setStory(res.data);
        }
      })
      .catch(() =>
        console.log("failed to write and roll story, story id: ", storyId)
      )
      .finally(() => setIsSubmitting(false));
  }

  function getNotice(idx) {
    if (canSkip && story.isAiSkipped) {
      return (
        <Text fontSize="lg" color="blue">
          {notify_both_skip}
        </Text>
      );
    } else if (canSkip) {
      return (
        <Text fontSize="lg" color="orange">
          {notify_skip}
        </Text>
      );
    } else if (story.isAiSkipped) {
      return (
        <Text fontSize="lg" color="green">
          {notify_skipped}
        </Text>
      );
    }
    return (
      <Text fontSize="md" color="gray.500">
        {idx == 0 && story?.leader === "student"
          ? instr_relay_start
          : wrapUpOptions === 2
            ? instr_relay_end
            : wrapUpOptions === 1
              ? instr_relay_end_opt
              : instr_relay_middle}
      </Text>
    );
  }

  return (
    <Container width="75%" maxW="none" mt={20}>
      <Heading size="lg" mb={4}>
        Impromptu Storytelling
      </Heading>
      <RelayPrompt
        storyTitle={storyTitle}
        starter={starter}
        instruction={instruction}
        extraInstruction={extraInstruction}
        status={status}
        round={round}
        url={url}
        genre={genre}
        skipAllowed={skipAllowed}
        maxWordCount={maxWordCount}
        minSegmentWordCount={minSegmentWordCount}
        maxSegmentWordCount={maxSegmentWordCount}
        startRelay={handleRelayStart}
        isSubmitting={isSubmitting}
      />
      {story?.relaySegments?.length > 0 ? (
        <Heading size="md" color="teal.700" mb={4}>
          Write & Roll
        </Heading>
      ) : (
        <Image
          src={game_not_start_img}
          alt="Impromptu story game is yet to start"
          borderRadius="md"
          boxSize="100%"
          objectFit="cover"
        />
      )}
      <Box mb={6}>
        <VStack align="stretch" spacing={6}>
          {story?.relaySegments?.map((segment, index) => (
            <Box
              key={index}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg={segment.runner === "Ivy" ? "gray.100" : "white"}
              boxShadow="sm"
            >
              {/* Segment Content */}
              {segment.status == "Done" && (
                <VStack
                  align="stretch"
                  spacing={4}
                  flexDirection={isMobile ? "column" : "row"}
                >
                  {/* Left Side */}
                  <Box flex={2 / 3}>
                    <Text fontSize="lg" whiteSpace="pre-wrap">
                      {segment.content}
                    </Text>
                  </Box>

                  {/* Right Side */}
                  <Box flex={1 / 3}>
                    {segment.runner === "student" ? (
                      <Text fontSize="sm" color="gray.600">
                        <strong>Comments: </strong>
                        {segment.comment}
                      </Text>
                    ) : (
                      <>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          <strong>Explain:</strong> {segment.explain}
                        </Text>
                        {story.status === "Done" && (
                          <Text fontSize="sm" color="blue">
                            <strong>Expect:</strong> {segment.expect}
                          </Text>
                        )}
                      </>
                    )}
                  </Box>
                </VStack>
              )}
              {segment.status === "Skipped" && (
                <Box>
                  {segment.runner === "student" ? (
                    <Flex>
                      <Box mt="2px">
                        <GiNightSleep color="orange" />
                      </Box>
                      <Text color="greed" fontWeight="bold" ml="4px">
                        {`I skipped a round!`}
                      </Text>
                    </Flex>
                  ) : (
                    <Flex>
                      <Box mt="2px">
                        <GiSadCrab color="red" />
                      </Box>
                      <Text color="green" fontWeight="bold" ml="4px">
                        {`Ivy skipped a round!`}
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}

              {/* Form for Student Segment */}
              {segment.runner === "student" &&
                segment.status !== "Done" &&
                segment.status !== "Skipped" && (
                  <Formik
                    initialValues={{ draft: segment.content || "" }}
                    onSubmit={(actions) => {
                      // console.log('Submitted:', values);
                      actions.resetForm();
                    }}
                  >
                    {({ values, handleChange }) => {
                      setWordCount(countWords(values.draft));
                      const isDisabled =
                        wordCount < minSegmentWordCount ||
                        wordCount > maxSegmentWordCount;

                      return (
                        <Form>
                          {/* Word Count Info */}
                          {/* <Text fontSize="md" color="gray.500">
                            {index == 0
                              ? instr_relay_start
                              : instr_relay_middle}
                          </Text> */}
                          {getNotice(index)}
                          <Text fontSize="sm" color="gray.500" mt={2}>
                            Word count:{" "}
                            <Text
                              as="span"
                              color={isDisabled ? "red" : "gray.500"}
                            >
                              {wordCount}
                            </Text>{" "}
                            (Min: {minSegmentWordCount}, Max:{" "}
                            {maxSegmentWordCount})
                          </Text>

                          {/* Textarea */}
                          <Field
                            as={Textarea}
                            name="draft"
                            fontSize="1.2em"
                            placeholder="Enter your segment..."
                            onChange={(e) => {
                              handleChange(e);
                              // setWordCount(countWords(e.target.value));
                            }}
                            rows={8}
                            // isDisabled={wordCount > maxSegmentWordCount}
                          />

                          {/* Buttons */}
                          <HStack mt={4} spacing={4}>
                            <Button
                              type="button"
                              colorScheme="gray"
                              isLoading={isSubmitting}
                              onClick={() => updateStory(values.draft)}
                            >
                              Save My Words
                            </Button>
                            {canSkip && (
                              <Button
                                type="button"
                                colorScheme="gray"
                                isLoading={isSubmitting}
                                onClick={() =>
                                  writeAndRollStory(values.draft, 3)
                                }
                              >
                                Skip My Turn
                              </Button>
                            )}
                            {/* last steps cannot roll */}
                            {wrapUpOptions != 2 && (
                              <Button
                                type="submit"
                                colorScheme="green"
                                isLoading={isSubmitting}
                                isDisabled={isDisabled || isSubmitting}
                                onClick={() =>
                                  writeAndRollStory(values.draft, 2)
                                }
                              >
                                Keep It Rolling
                              </Button>
                            )}
                            {/* first steps cannot wrap up */}
                            {wrapUpOptions > 0 && (
                              <Button
                                type="button"
                                colorScheme="green"
                                isLoading={isSubmitting}
                                isDisabled={isDisabled || isSubmitting}
                                onClick={() =>
                                  writeAndRollStory(values.draft, 4)
                                }
                              >
                                The End
                              </Button>
                            )}
                          </HStack>
                        </Form>
                      );
                    }}
                  </Formik>
                )}
            </Box>
          ))}
        </VStack>
      </Box>
    </Container>
  );
}

export default StoryRelayPage;
